import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, User as UserIcon, Bot, Paperclip, X, FileText, Mic, Square } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const MAX_ATTS = 4;
// Read a file into a Gemini-ready part; images are downscaled client-side to keep payloads small.
const fileToPart = (file) => new Promise((resolve, reject) => {
  const isImg = (file.type || "").startsWith("image/");
  const reader = new FileReader();
  reader.onerror = reject;
  if (!isImg) {
    reader.onload = () => resolve({ name: file.name, mime: file.type || "application/pdf", data: String(reader.result).split(",")[1], preview: null, isPdf: true });
    reader.readAsDataURL(file);
    return;
  }
  reader.onload = () => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      const max = 1600; let w = img.width, h = img.height;
      if (w > max || h > max) { const s = Math.min(max / w, max / h); w = Math.round(w * s); h = Math.round(h * s); }
      const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
      resolve({ name: file.name, mime: "image/jpeg", data: dataUrl.split(",")[1], preview: dataUrl, isPdf: false });
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const SUGGESTIONS = [
  "ما هي الأصناف الأكثر مبيعاً هذا الأسبوع؟",
  "ما مجموع المبيعات اليوم؟",
  "أي عملاء لديهم أعلى مشتريات؟",
  "أي أصناف قاربت على انتهاء الصلاحية؟",
  "أي أصناف تحت الحد الأدنى وأحتاج طلبها؟",
  "عدّل أسعار بيع أصناف فئة المسكنات بنسبة 10% بدون تقريب",
  "أنشئ قيدًا: مدين مصروفات تشغيلية 100000 ودائن الصندوق 100000",
  "أضف حسابًا باسم مصروفات الشحن برمز 5300 من نوع expense",
  "لخّص لي حالة الشركة الحالية بنقاط",
];

export default function AIChat() {
  const { currentCompany } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [atts, setAtts] = useState([]);
  const [recording, setRecording] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const speechBaseRef = useRef("");
  const recordingRef = useRef(false);

  const toggleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("المتصفح لا يدعم تحويل الصوت إلى نص. جرّب Chrome أو Edge.");
      return;
    }
    if (recordingRef.current) {
      recordingRef.current = false;
      setRecording(false);
      recognitionRef.current?.stop();
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "ar-SA";
    recognition.continuous = true;
    recognition.interimResults = true;
    speechBaseRef.current = input.trim();
    recognition.onstart = () => { recordingRef.current = true; setRecording(true); };
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) transcript += event.results[i][0].transcript;
      const finalText = `${speechBaseRef.current}${speechBaseRef.current && transcript ? " " : ""}${transcript}`.trim();
      setInput(finalText);
    };
    recognition.onerror = (event) => {
      recordingRef.current = false;
      setRecording(false);
      if (event.error !== "aborted" && event.error !== "no-speech") toast.error("تعذّر الوصول إلى الميكروفون أو فهم الصوت");
    };
    recognition.onend = () => {
      if (recordingRef.current) {
        try { recognition.start(); } catch {}
      } else setRecording(false);
    };
    recognitionRef.current = recognition;
    try { recognition.start(); } catch { toast.error("تعذّر بدء التسجيل الصوتي"); }
  };

  useEffect(() => () => {
    recordingRef.current = false;
    recognitionRef.current?.stop();
  }, []);

  const loadHistory = async () => {
    try { const { data } = await api.get("/ai/history"); 
      const msgs = [];
      data.forEach(m => { msgs.push({role:"user", text:m.question}); msgs.push({role:"assistant", text:m.answer}); });
      setMessages(msgs);
    } catch {}
  };
  useEffect(() => { loadHistory(); }, [currentCompany?.id]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, atts]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of files) {
      if (atts.length >= MAX_ATTS) { toast.error(`الحد الأقصى ${MAX_ATTS} مرفقات`); break; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name}: الحجم كبير (الحد 8MB)`); continue; }
      try { const p = await fileToPart(f); setAtts(a => [...a, p].slice(0, MAX_ATTS)); }
      catch { toast.error(`تعذّر قراءة ${f.name}`); }
    }
  };
  const removeAtt = (i) => setAtts(a => a.filter((_, idx) => idx !== i));

  const ask = async (q) => {
    const text = (q || input).trim();
    if ((!text && atts.length === 0) || loading) return;
    const history = messages.map(m => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));
    const payloadAtts = atts.map(a => ({ name: a.name, mime: a.mime, data: a.data }));
    const shownAtts = atts.map(a => ({ name: a.name, preview: a.preview, isPdf: a.isPdf }));
    setMessages(m => [...m, { role: "user", text: text || "(مرفقات)", atts: shownAtts }]);
    setInput("");
    setAtts([]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: text, history, attachments: payloadAtts });
      setMessages(m => [...m, { role: "assistant", text: data.reply || data.answer || "لا يوجد رد" }]);
    } catch(e) {
      const err = formatErr(e);
      toast.error(err);
      setMessages(m => [...m, { role: "assistant", text: `تعذر الحصول على إجابة: ${err}` }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 h-[calc(100vh-8rem)] flex flex-col" data-testid="ai-chat-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">المساعد الذكي</h1>
          <p className="text-sm text-muted-foreground">اسأل عن أي شي في شركتك بلغة طبيعية</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto border border-border rounded-md bg-card p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <Bot className="w-12 h-12 mx-auto text-primary" />
            <div className="text-lg font-heading font-bold">أهلاً! كيف يمكنني مساعدتك اليوم؟</div>
            <div className="text-sm text-muted-foreground">جرّب أحد الأسئلة السريعة:</div>
            <div className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={()=>ask(s)} data-testid={`suggestion-${s.slice(0,10)}`}
                  className="text-start text-sm p-3 border border-border rounded-md hover:border-primary hover:bg-accent transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <div className={`w-8 h-8 rounded-md grid place-items-center flex-shrink-0 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-emerald-100 text-emerald-700"}`}>
              {m.role === "user" ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
            </div>
            <div className={`max-w-[80%] p-3 rounded-md text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
              {m.atts && m.atts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid={`msg-atts-${i}`}>
                  {m.atts.map((a, ai) => a.preview
                    ? <img key={ai} src={a.preview} alt={a.name} className="w-20 h-20 object-cover rounded border border-white/30" />
                    : <span key={ai} className="inline-flex items-center gap-1 text-xs bg-black/10 rounded px-2 py-1"><FileText className="w-3.5 h-3.5" /> {a.name}</span>)}
                </div>
              )}
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-md bg-emerald-100 text-emerald-700 grid place-items-center">
              <Bot className="w-4 h-4" />
            </div>
            <div className="p-3 bg-muted rounded-md flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> جاري التفكير...
            </div>
          </div>
        )}
      </div>

      <form onSubmit={(e)=>{e.preventDefault(); ask();}} className="space-y-2">
        {atts.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="ai-pending-atts">
            {atts.map((a, i) => (
              <div key={i} className="relative group">
                {a.preview
                  ? <img src={a.preview} alt={a.name} className="w-16 h-16 object-cover rounded-md border border-border" />
                  : <div className="w-16 h-16 rounded-md border border-border bg-muted flex flex-col items-center justify-center gap-1 p-1"><FileText className="w-5 h-5 text-primary" /><span className="text-[9px] truncate max-w-[56px]">{a.name}</span></div>}
                <button type="button" onClick={()=>removeAtt(i)} data-testid={`ai-att-remove-${i}`}
                  className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-destructive text-white grid place-items-center shadow">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onPick} data-testid="ai-file-input" />
          <Button type="button" variant="outline" className="h-11 w-11 p-0 flex-shrink-0" onClick={()=>fileRef.current?.click()} disabled={loading || atts.length >= MAX_ATTS} data-testid="ai-attach" title="إرفاق صورة أو ملف PDF">
            <Paperclip className="w-4 h-4" />
          </Button>
          <Button type="button" variant={recording ? "destructive" : "outline"} className={`h-11 w-11 p-0 flex-shrink-0 ${recording ? "animate-pulse" : ""}`} onClick={toggleVoiceInput} disabled={loading} data-testid="ai-voice" title={recording ? "إيقاف التسجيل" : "تحدث إلى المساعد"}>
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </Button>
          <Input value={input} onChange={(e)=>setInput(e.target.value)} placeholder="اكتب سؤالك أو أرفق صورة/ملف..." data-testid="ai-input" className="h-11" disabled={loading} />
          <Button type="submit" disabled={loading || (!input.trim() && atts.length === 0)} className="h-11" data-testid="ai-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </form>
    </div>
  );
}
